import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUserLearningPaths } from '../services/firebase';
import { getLevel } from '../utils/levelSystem';
import './Analytics.css';

function Analytics() {
    const { currentUser, userData } = useAuth();
    const [learningPaths, setLearningPaths] = useState([]);
    const [timeframe, setTimeframe] = useState('week'); // week, month, all
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadAnalyticsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, userData, timeframe]);

    const loadAnalyticsData = async () => {
        if (!currentUser || !userData) {
            setLoading(false);
            return;
        }

        try {
            const pathsResult = await getUserLearningPaths(currentUser.uid);
            if (pathsResult.success) {
                setLearningPaths(pathsResult.data);
            }
        } catch (error) {
            console.error('Error loading analytics:', error);
        }
        setLoading(false);
    };

    // Generate mock XP progression data (in real app, this would come from Firebase)
    const getXPProgression = () => {
        const days = timeframe === 'week' ? 7 : timeframe === 'month' ? 30 : 90;
        const data = [];
        let currentXP = Math.max(0, (userData?.totalXP || 0) - (days * 15));

        for (let i = 0; i < days; i++) {
            const increment = Math.floor(Math.random() * 30) + 5;
            currentXP += increment;
            data.push({
                day: i + 1,
                xp: Math.min(currentXP, userData?.totalXP || 0)
            });
        }

        return data;
    };

    // Calculate performance metrics
    const getPerformanceMetrics = () => {
        const stats = userData?.stats || {};
        const totalQuestions = stats.totalQuestions || 100;
        const correctAnswers = stats.correctAnswers || 75;
        const lessonsCompleted = stats.lessonsCompleted || 8;

        return {
            accuracy: Math.round((correctAnswers / totalQuestions) * 100) || 0,
            completionRate: Math.round((lessonsCompleted / (learningPaths.length * 5 || 1)) * 100) || 0,
            averageScore: Math.round((correctAnswers / totalQuestions) * 100) || 0,
            studyTime: Math.floor((stats.studyTime || 3600) / 60) // minutes
        };
    };

    // Get subject performance breakdown
    const getSubjectPerformance = () => {
        return learningPaths.map(path => {
            const completedModules = userData?.completedModules?.filter(
                m => m.startsWith(`${path.topic}_`)
            ).length || 0;
            const totalModules = path.totalModules || 5;
            const progress = Math.round((completedModules / totalModules) * 100);

            return {
                subject: path.topic,
                progress: progress,
                modules: completedModules,
                total: totalModules
            };
        }).sort((a, b) => b.progress - a.progress);
    };

    // Get study streak data
    const getStreakData = () => {
        const currentStreak = userData?.streak || 0;
        const longestStreak = Math.max(currentStreak, Math.floor(currentStreak * 1.5)); // Mock longest streak

        return {
            current: currentStreak,
            longest: longestStreak,
            thisWeek: Math.min(currentStreak, 7)
        };
    };

    if (loading) {
        return (
            <div className="analytics-loading">
                <div className="spinner"></div>
                <p>Loading analytics...</p>
            </div>
        );
    }

    const xpData = getXPProgression();
    const maxXP = Math.max(...xpData.map(d => d.xp));
    const metrics = getPerformanceMetrics();
    const subjectPerformance = getSubjectPerformance();
    const streakData = getStreakData();

    return (
        <div className="analytics-container">
            {/* Header */}
            <div className="analytics-header">
                <h1>📊 Learning Analytics</h1>
                <p className="analytics-subtitle">Track your progress and performance</p>
            </div>

            {/* Timeframe Selector */}
            <div className="timeframe-selector">
                <button
                    className={`timeframe-btn ${timeframe === 'week' ? 'active' : ''}`}
                    onClick={() => setTimeframe('week')}
                >
                    Last 7 Days
                </button>
                <button
                    className={`timeframe-btn ${timeframe === 'month' ? 'active' : ''}`}
                    onClick={() => setTimeframe('month')}
                >
                    Last 30 Days
                </button>
                <button
                    className={`timeframe-btn ${timeframe === 'all' ? 'active' : ''}`}
                    onClick={() => setTimeframe('all')}
                >
                    All Time
                </button>
            </div>

            {/* Key Metrics Grid */}
            <div className="metrics-grid">
                <div className="metric-card">
                    <div className="metric-icon">🎯</div>
                    <div className="metric-value">{metrics.accuracy}%</div>
                    <div className="metric-label">Accuracy</div>
                    <div className="metric-bar">
                        <div
                            className="metric-bar-fill accuracy"
                            style={{ width: `${metrics.accuracy}%` }}
                        />
                    </div>
                </div>

                <div className="metric-card">
                    <div className="metric-icon">✅</div>
                    <div className="metric-value">{metrics.completionRate}%</div>
                    <div className="metric-label">Completion Rate</div>
                    <div className="metric-bar">
                        <div
                            className="metric-bar-fill completion"
                            style={{ width: `${metrics.completionRate}%` }}
                        />
                    </div>
                </div>

                <div className="metric-card">
                    <div className="metric-icon">⏱️</div>
                    <div className="metric-value">{metrics.studyTime}</div>
                    <div className="metric-label">Minutes Studied</div>
                    <div className="metric-bar">
                        <div
                            className="metric-bar-fill time"
                            style={{ width: `${Math.min((metrics.studyTime / 120) * 100, 100)}%` }}
                        />
                    </div>
                </div>

                <div className="metric-card">
                    <div className="metric-icon">🔥</div>
                    <div className="metric-value">{streakData.current}</div>
                    <div className="metric-label">Day Streak</div>
                    <div className="metric-bar">
                        <div
                            className="metric-bar-fill streak"
                            style={{ width: `${Math.min((streakData.current / 30) * 100, 100)}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* XP Progress Chart */}
            <div className="chart-section">
                <h2>💎 XP Progress</h2>
                <p className="chart-subtitle">Your learning journey over time</p>
                <div className="xp-chart">
                    <div className="chart-y-axis">
                        <span>{maxXP}</span>
                        <span>{Math.floor(maxXP * 0.75)}</span>
                        <span>{Math.floor(maxXP * 0.5)}</span>
                        <span>{Math.floor(maxXP * 0.25)}</span>
                        <span>0</span>
                    </div>
                    <div className="chart-content">
                        <div className="chart-grid">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="grid-line" />
                            ))}
                        </div>
                        <div className="chart-bars">
                            {xpData.map((data, index) => (
                                <div key={index} className="bar-container">
                                    <div
                                        className="bar"
                                        style={{
                                            height: `${(data.xp / maxXP) * 100}%`,
                                            animationDelay: `${index * 0.05}s`
                                        }}
                                    >
                                        <div className="bar-tooltip">{data.xp} XP</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="chart-total">
                    <span className="total-label">Total XP Earned:</span>
                    <span className="total-value">{userData?.totalXP || 0} XP</span>
                </div>
            </div>

            {/* Subject Performance */}
            {subjectPerformance.length > 0 && (
                <div className="chart-section">
                    <h2>📚 Subject Performance</h2>
                    <p className="chart-subtitle">Progress across your learning paths</p>
                    <div className="subject-performance-list">
                        {subjectPerformance.map((subject, index) => (
                            <div key={index} className="subject-performance-item">
                                <div className="subject-header">
                                    <span className="subject-name">{subject.subject}</span>
                                    <span className="subject-stats">
                                        {subject.modules}/{subject.total} modules
                                    </span>
                                </div>
                                <div className="subject-progress-bar">
                                    <div
                                        className="subject-progress-fill"
                                        style={{
                                            width: `${subject.progress}%`,
                                            animationDelay: `${index * 0.1}s`
                                        }}
                                    />
                                    <span className="subject-percentage">{subject.progress}%</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Performance Insights */}
            <div className="insights-grid">
                <div className="insight-card">
                    <h3>🏆 Achievements</h3>
                    <div className="insight-stats">
                        <div className="insight-stat">
                            <span className="stat-number">{getLevel(userData?.totalXP || 0).level}</span>
                            <span className="stat-label">Current Level</span>
                        </div>
                        <div className="insight-stat">
                            <span className="stat-number">{userData?.completedModules?.length || 0}</span>
                            <span className="stat-label">Modules Complete</span>
                        </div>
                    </div>
                </div>

                <div className="insight-card">
                    <h3>📈 Trends</h3>
                    <div className="trend-list">
                        {metrics.accuracy >= 80 && (
                            <div className="trend-item positive">
                                <span className="trend-icon">📈</span>
                                <span>High accuracy rate!</span>
                            </div>
                        )}
                        {streakData.current >= 7 && (
                            <div className="trend-item positive">
                                <span className="trend-icon">🔥</span>
                                <span>Weekly streak achieved!</span>
                            </div>
                        )}
                        {metrics.studyTime >= 60 && (
                            <div className="trend-item positive">
                                <span className="trend-icon">⏱️</span>
                                <span>Dedicated study time!</span>
                            </div>
                        )}
                        {learningPaths.length >= 3 && (
                            <div className="trend-item positive">
                                <span className="trend-icon">📚</span>
                                <span>Multi-subject learner!</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="insight-card">
                    <h3>🎯 Goals</h3>
                    <div className="goal-list">
                        <div className="goal-item">
                            <span className="goal-icon">
                                {userData?.totalXP >= 1000 ? '✅' : '⏳'}
                            </span>
                            <span>Reach 1000 XP</span>
                        </div>
                        <div className="goal-item">
                            <span className="goal-icon">
                                {streakData.current >= 30 ? '✅' : '⏳'}
                            </span>
                            <span>30-day streak</span>
                        </div>
                        <div className="goal-item">
                            <span className="goal-icon">
                                {userData?.completedModules?.length >= 10 ? '✅' : '⏳'}
                            </span>
                            <span>Complete 10 modules</span>
                        </div>
                        <div className="goal-item">
                            <span className="goal-icon">
                                {metrics.accuracy >= 90 ? '✅' : '⏳'}
                            </span>
                            <span>90% accuracy</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Analytics;
